import type {
  TransportConnection,
  TransportOption,
  TripItineraryData,
  TripItineraryItem,
  TripItemWithoutDay,
} from 'tripPlanner/api/types'

interface TripListItineraryItemUpdaters {
  updateItem: <Item extends TripItineraryItem | TripItemWithoutDay>(item: Item) => Item
  updateTransportOption: (option: TransportOption) => TransportOption
}

function updateTransportOption(
  option: TransportOption,
  updater: (option: TransportOption) => TransportOption,
): TransportOption {
  const nextOption = updater(option)
  const nextReturnLeg = option.returnLeg ? updateTransportOption(option.returnLeg, updater) : undefined

  if (nextOption === option && nextReturnLeg === option.returnLeg) return option

  return {
    ...nextOption,
    ...(nextReturnLeg ? { returnLeg: nextReturnLeg } : {}),
  }
}

function updateTransportConnection(
  connection: TransportConnection,
  updater: (option: TransportOption) => TransportOption,
): TransportConnection {
  let changed = false
  const options = connection.options.map((option) => {
    const nextOption = updateTransportOption(option, updater)
    if (nextOption !== option) changed = true
    return nextOption
  })

  return changed ? { ...connection, options } : connection
}

export function updateTripItineraryItem(
  itineraryData: TripItineraryData | undefined,
  updaters: TripListItineraryItemUpdaters,
): TripItineraryData | undefined {
  if (!itineraryData) return itineraryData

  let changed = false
  const stops = itineraryData.stops.map((stop) => {
    let stopChanged = false
    const days = stop.days.map((day) => {
      let dayChanged = false
      const items = day.items.map((item) => {
        const nextItem = updaters.updateItem(item)
        if (nextItem !== item) dayChanged = true
        return nextItem
      })

      if (!dayChanged) return day
      stopChanged = true
      return { ...day, items }
    })

    const itemsWithoutDay = stop.itemsWithoutDay.map((item) => {
      const nextItem = updaters.updateItem(item)
      if (nextItem !== item) stopChanged = true
      return nextItem
    })

    const transports = stop.transports?.map((connection) => {
      const nextConnection = updateTransportConnection(connection, updaters.updateTransportOption)
      if (nextConnection !== connection) stopChanged = true
      return nextConnection
    })

    if (!stopChanged) return stop
    changed = true
    return {
      ...stop,
      days,
      itemsWithoutDay,
      ...(transports ? { transports } : {}),
    }
  })

  return changed ? { ...itineraryData, stops } : itineraryData
}
